/**
 * Renko Rotations chart (canonical, render-only).
 *
 * Renders the single canonical renko series (8pt bricks, strength 3) and its
 * rotation overlay (zig-zag legs + 70/90 leg-size bands). All swing/leg/threshold
 * math is computed server-side and served from cache; this page only fetches and
 * renders. The user scrolls/zooms the chart natively.
 *
 * Time axis: renko is not time-uniform and bricks can share a second (bursts /
 * multi-brick ticks), which lightweight-charts forbids (times must be strictly
 * increasing). We map each brick to its REAL formation-start second
 * (open_ts // 1e9), de-collided to strictly increasing (t_i = max(start_i,
 * t_{i-1}+1)). lightweight-charts spaces bars by index (not by time gap), so the
 * chart looks/behaves exactly like the main chart: real ET dates in the crosshair
 * tooltip, real HH:MM on the axis, and a date picker to page history back. Keying
 * on the formation-start second means a live forming brick and the brick it
 * becomes share the same slot, so the forming bar finalizes in place.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ESChart, IndicatorSidePanel, TimezoneSelector } from '../components';
import { useLevelIndicators } from '../hooks';
import { CONFIG } from '../config';
import { authFetch } from '../auth/authFetch';
import { useLiveRenkoBricks } from '../hooks/useLiveRenkoBricks';
import { getLatestSessionDate, snapToWeekday, stepWeekday } from '../utils/market';
import { RENKO_GROUPS, RENKO_INDICATOR_DEFAULTS } from './renkoIndicators';
import '../components/NavigationBar.css';

const HISTORY_DAYS = 90;  // earliest selectable start when paging history back (backend clamps ~92)

// Real formation-start second of a brick (open_ts is nanoseconds; fall back to
// the completion `time` if absent).
function brickStartSec(b) {
  if (b == null) return null;
  if (b.open_ts != null) return Math.floor(b.open_ts / 1e9);
  if (b.time != null) return Math.floor(b.time > 1e12 ? b.time / 1e9 : b.time);
  return null;
}

// Completion second of a brick == leg.start_ts/end_ts (swing.formation_ts = brick `time` =
// close_ts//1e9). Used to map legs to the de-collided axis BY TIME when their server-side brick
// index outruns the locally-loaded brick array (see resolveX).
function brickCloseSec(b) {
  if (b == null) return null;
  if (b.time != null) return Math.floor(b.time > 1e12 ? b.time / 1e9 : b.time);
  if (b.close_ts != null) return Math.floor(b.close_ts / 1e9);
  return null;
}

// Strictly-increasing display seconds for a brick list (de-collide bursts).
function decollide(bricks) {
  const out = new Array(bricks.length);
  let prev = -Infinity;
  for (let i = 0; i < bricks.length; i++) {
    const s = brickStartSec(bricks[i]);
    const t = Math.max(s == null ? prev + 1 : s, prev + 1);
    out[i] = t;
    prev = t;
  }
  return out;
}


export default function RenkoRotationsPage() {
  const [bricks, setBricks] = useState([]);       // REST snapshot (live bricks live in the chart's candlesRef)
  // The full v2 rotations envelope {meta, rotations}; body + meta derived below
  const [rotationsPayload, setRotationsPayload] = useState(null);
  const rotations = rotationsPayload?.rotations ?? null;
  const rotationsMeta = rotationsPayload?.meta ?? null;
  const [startDate, setStartDate] = useState(null); // null = default (current session day)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Reserve chart space for the indicator panel so the price axis is not covered
  const [panelOpen, setPanelOpen] = useState(false);
  // Global grid-lines setting (edited in the main chart's Candle Colors dialog);
  // read once at mount, same static treatment this page gives candle colors.
  const [hideGridLines] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hideGridLines') || 'false') === true; } catch (e) { return false; }
  });
  // Served renko brick size (the chart fetches + subscribes to this size). Shared with the main
  // chart's Rotations "Brick" picker via the `indicators` blob (key orderflowRotationBrickSize) so the
  // two stay in sync. Must be one of the backend RENKO_SERVED_SIZES (8 default, 4 finer).
  const [brickSize, setBrickSize] = useState(() => {
    try {
      const ind = JSON.parse(localStorage.getItem('indicators') || '{}');
      if (ind && ind.orderflowRotationBrickSize) return Number(ind.orderflowRotationBrickSize);
    } catch (e) { /* ignore */ }
    return 8;
  });

  // Timezone for the chart time axis (UTC/ET). Shared with the main chart via the
  // 'timezone' localStorage key so the two toolbars stay consistent.
  const [timezone, setTimezone] = useState(() => {
    try { const v = localStorage.getItem('timezone'); if (v) return JSON.parse(v); } catch (e) { /* ignore */ }
    return 'ET';
  });

  // Planning-style indicator panel state (persisted to its own localStorage key,
  // independent of the main/planning charts). Per-candle profiles are excluded.
  // The rotation-overlay toggles (rotLegs/rotReach/rotBias) live here too now, so the
  // whole control set is in the side panel instead of the top toolbar.
  const [indicators, setIndicators] = useState(() => {
    try {
      const saved = localStorage.getItem('renkoIndicators');
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...RENKO_INDICATOR_DEFAULTS, ...parsed };
      }
    } catch (e) { /* ignore */ }
    return RENKO_INDICATOR_DEFAULTS;
  });
  const handleIndicatorToggle = useCallback((id, value) => {
    setIndicators((prev) => {
      const next = { ...prev, [id]: value };
      try { localStorage.setItem('renkoIndicators', JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  }, []);
  // Bulk apply a saved preset snapshot from the side panel (one atomic merge).
  const handleApplyIndicators = useCallback((snapshot) => {
    setIndicators((prev) => {
      const next = { ...prev, ...snapshot };
      try { localStorage.setItem('renkoIndicators', JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  }, []);
  const [greeksLoading, setGreeksLoading] = useState(false);
  const [baselineLoading, setBaselineLoading] = useState(false);
  // Per-horizon refit health of the live-curve rows (side-panel state
  // line). Renko timeframes skip the live-curves SSE block, so this only
  // updates via the REST current-row seed when the plugin fetches.
  const [liveCurvesHealth, setLiveCurvesHealth] = useState(null);

  const updateCandleRef = useRef(null);   // chart's series.update (from onChartReady)
  const liveTimeRef = useRef(null);       // last committed display second for live appends
  const startDateRef = useRef(startDate); // live rotations push only applies in the default (current-session) view
  const loadingRef = useRef(false);       // ignore live rotations pushes mid-(re)load
  const pendingRotationsRef = useRef(null); // a live rotations push that arrived mid-load, replayed when load() finishes
  const pendingBricksRef = useRef([]);    // completed bricks that arrived mid-load, replayed (deduped by close_ts) when load() finishes
  const lastGapReloadRef = useRef(0);     // throttle for SSE gap-marker reloads
  const brickSizeRef = useRef(brickSize); // so the stable `load` closure always fetches the current size

  // Current session day -- the only day rendered by default (like the main /
  // planning charts). Held in state + repolled so it advances at the 18:00 ET
  // Globex roll. The reach thresholds are still measured over the trailing 30
  // days server-side; only the rendered window is a single day.
  const [sessionDate, setSessionDate] = useState(getLatestSessionDate);
  const sessionDateRef = useRef(sessionDate);
  const defaultStart = sessionDate;   // default view = current session day only
  // Earliest selectable start: the backend clamps a custom span to ~92 days, so
  // bound the picker to match (cache only holds the recent window anyway).
  const minDate = useMemo(() => {
    const d = new Date(sessionDate + 'T12:00:00');
    d.setDate(d.getDate() - HISTORY_DAYS);
    return snapToWeekday(d.toISOString().split('T')[0]);
  }, [sessionDate]);
  const effectiveStart = startDate || defaultStart;

  useEffect(() => { startDateRef.current = startDate; }, [startDate]);
  useEffect(() => { brickSizeRef.current = brickSize; }, [brickSize]);
  useEffect(() => { sessionDateRef.current = sessionDate; }, [sessionDate]);

  const load = useCallback(async (start) => {
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (start) params.set('start_date', start);
      params.set('brick_size', String(brickSizeRef.current));
      const qs = `?${params.toString()}`;
      const [bRes, rRes] = await Promise.all([
        authFetch(`${CONFIG.apiUrl}/api/es/renko-bricks${qs}`),
        authFetch(`${CONFIG.apiUrl}/api/es/renko-rotations${qs}`),
      ]);
      const bJson = bRes.ok ? await bRes.json() : { bricks: [] };
      const rJson = rRes.ok ? await rRes.json() : null;
      setBricks(Array.isArray(bJson.bricks) ? bJson.bricks : []);
      setRotationsPayload(rJson);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      // Replay completed bricks that arrived mid-load BEFORE clearing
      // loadingRef, so any brick landing after the clear appends strictly
      // later and the array stays chronological. Dedupe by close_ts (ns,
      // globally unique; `time` is second-granular and can collide) against
      // the wholesale REST snapshot, which may already include them.
      if (pendingBricksRef.current.length) {
        const buffered = pendingBricksRef.current;
        pendingBricksRef.current = [];
        setBricks((prev) => {
          const have = new Set(prev.map((b) => b.close_ts).filter((v) => v != null));
          const fresh = buffered.filter((b) => b.close_ts == null || !have.has(b.close_ts));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
      }
      setLoading(false);
      loadingRef.current = false;
      // Replay the most recent live rotations push that arrived mid-load (default view
      // only), so a pivot confirmed during the REST round-trip isn't dropped by the gate
      // below until the next push/refresh. Runs AFTER the REST setRotations above, and the
      // buffered push is >= as fresh, so overwriting is correct.
      if (pendingRotationsRef.current && startDateRef.current == null) {
        setRotationsPayload(pendingRotationsRef.current);
      }
      pendingRotationsRef.current = null;
    }
  }, []);

  // (Re)load on mount and whenever the start date changes. Live bricks + rotations
  // then arrive via SSE.
  useEffect(() => { load(startDate); }, [load, startDate, brickSize]);

  // Detect the 18:00 ET session roll (~15s, like the planning chart) so the
  // default view auto-advances to the new session day and reloads. The no-param
  // default URL has no date in it, so it won't refetch on its own -- reload
  // explicitly. Historic views (startDate set) stay frozen on their range.
  useEffect(() => {
    const id = setInterval(() => {
      const next = getLatestSessionDate();
      if (next === sessionDateRef.current) return;   // no roll -> no churn
      sessionDateRef.current = next;
      setSessionDate(next);
      if (startDateRef.current == null) load(null);
    }, 15000);
    return () => clearInterval(id);
  }, [load]);

  // Real de-collided display seconds, shared by the candles, the live tail, and
  // the rotation-leg x-mapping (legs index into this same brick array).
  const displayTimes = useMemo(() => decollide(bricks), [bricks]);

  // Resolve a leg/segment endpoint to a de-collided brick display time. In-range brick index ->
  // the exact displayTimes[idx] (unchanged behaviour). Out-of-range -- the live rotations push
  // (server's authoritative brick count) can reference more bricks than the locally-appended
  // `bricks` array holds yet -- resolves by the brick CLOSE second (== leg start_ts/end_ts) to the
  // matching loaded brick, else the nearest loaded brick / live edge. So a freshly-confirmed leg
  // renders at ~its true time instead of being dropped, then snaps exact as bricks fold in. A null
  // ts (bias segments carry no ts) -> live edge.
  const resolveX = useMemo(() => {
    const n = bricks.length;
    const secToDisplay = new Map();
    const secList = new Array(n);
    for (let i = 0; i < n; i++) {
      const s = brickCloseSec(bricks[i]);
      secList[i] = s;
      if (s != null) secToDisplay.set(s, displayTimes[i]);
    }
    const nearestDisplay = (ts) => {                 // largest i with secList[i] <= ts; clamp to ends
      if (!n) return null;
      if (ts == null || secList[0] == null || ts <= secList[0]) return displayTimes[0];
      if (ts >= secList[n - 1]) return displayTimes[n - 1];
      let lo = 0, hi = n - 1;
      while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (secList[mid] != null && secList[mid] <= ts) lo = mid; else hi = mid - 1;
      }
      return displayTimes[lo];
    };
    return (idx, ts) => {
      if (idx != null && idx >= 0 && idx < n) return displayTimes[idx];   // exact, in range
      if (ts != null) {
        const hit = secToDisplay.get(ts);
        return hit != null ? hit : nearestDisplay(ts);                    // by time (drifted/missing)
      }
      return n ? displayTimes[n - 1] : null;                             // no ts -> live edge
    };
  }, [bricks, displayTimes]);

  // Track the last committed display second so SSE bricks append at the next slot.
  useEffect(() => {
    liveTimeRef.current = displayTimes.length ? displayTimes[displayTimes.length - 1] : null;
  }, [displayTimes]);

  // Capture the chart's updateCandle (series.update) for live brick appends.
  const onChartReady = useCallback((chart, series, container, updateCandle) => {
    updateCandleRef.current = updateCandle;
  }, []);

  // Display second for a live brick: its de-collided formation-start second,
  // never below the last committed slot + 1. The forming brick and the brick it
  // becomes share open_ts, so both resolve to the same slot (in-place finalize).
  const liveTimeFor = useCallback((brick) => {
    const last = liveTimeRef.current;
    const floor = (last == null) ? -Infinity : last + 1;
    const base = brickStartSec(brick);
    let t = (base == null) ? floor : Math.max(base, floor);
    if (!isFinite(t)) t = Math.floor(Date.now() / 1000);
    return t;
  }, []);

  // Realtime via SSE (no polling):
  //  - renko_forming : the in-progress brick, drawn but NOT committed (isComplete=false)
  //  - renko_brick   : a completed brick, committed in place at the same slot (isComplete=true)
  //  - renko_rotations: swap the overlay (current-session indices, default view only)
  useLiveRenkoBricks(
    useCallback((brick) => {           // completed brick
      if (!brick) return;
      // Mid-(re)load: the REST snapshot about to replace `bricks` may already
      // contain this brick, and a blind append after the wholesale swap would
      // duplicate it and shift the index-mapped rotation legs. Buffer and let
      // load() replay it deduped by close_ts.
      if (loadingRef.current) { pendingBricksRef.current.push(brick); return; }
      if (!updateCandleRef.current) return;
      const t = liveTimeFor(brick);
      updateCandleRef.current({
        time: t, open: brick.open, high: brick.high, low: brick.low,
        close: brick.close, value: brick.close,
      }, true);
      liveTimeRef.current = t;
      // Also fold the completed brick into `bricks` so displayTimes + the
      // rotation-leg index mapping stay aligned with the live tail (decollide
      // gives this brick the same slot `t` that updateCandle just drew, so no
      // jump/duplicate). The next live rotations push then maps correctly.
      setBricks((prev) => [...prev, brick]);
    }, [liveTimeFor]),
    useCallback((brick) => {           // forming brick (in-progress)
      if (!updateCandleRef.current || !brick) return;
      const t = liveTimeFor(brick);    // same slot until it completes (does not advance liveTimeRef)
      updateCandleRef.current({
        time: t, open: brick.open, high: brick.high, low: brick.low,
        close: brick.close, value: brick.close,
      }, false);
    }, [liveTimeFor]),
    useCallback((payload) => {
      // A live push always describes TODAY's session, so apply it only in the default
      // (current-session) view -- never overwrite a historic paged view whose axis has no
      // today times. Endpoint mapping is now by time (resolveX), so a brief brick-count lag
      // no longer drops the newest legs. If a (re)load is in flight, buffer the push and
      // replay it when load() finishes instead of dropping it (load swaps `bricks` async).
      if (!payload || startDateRef.current != null) return;
      if (loadingRef.current) { pendingRotationsRef.current = payload; return; }
      setRotationsPayload(payload);
    }, []),
    true,        // enabled
    brickSize,   // subscribe to es:renko:{brickSize}; resubscribes on change
    useCallback(() => {
      // SSE gap marker: a completed brick was evicted from this client's queue
      // (stalled/backgrounded tab), leaving a permanent hole that misaligns the
      // index-mapped rotation legs. Heal with the same wholesale REST reload the
      // page already uses; throttled so a burst of markers fires one reload.
      if (startDateRef.current != null) return;   // historic view has no live tail
      const now = Date.now();
      if (now - lastGapReloadRef.current < 5000) return;
      lastGapReloadRef.current = now;
      console.warn('[RenkoRotations] SSE gap marker received, reloading bricks');
      load(null);
    }, [load]),
  );

  // Bricks -> real-time candlesticks (wickless: high/low are body bounds).
  const candles = useMemo(() => (
    bricks.map((b, i) => ({
      time: displayTimes[i],
      open: b.open, high: b.high, low: b.low, close: b.close,
      value: b.close,
    }))
  ), [bricks, displayTimes]);

  // Rotation overlay: legs + bias segments mapped to the de-collided brick time axis via resolveX
  // (by index when in range, by time when a live push outruns the loaded bricks). Leg `reach`
  // (extended/typical/short) and the bias segments are computed server-side.
  const rotationData = useMemo(() => {
    const legsRaw = (rotations && Array.isArray(rotations.legs)) ? rotations.legs : [];
    const segsRaw = (rotations && rotations.bias && Array.isArray(rotations.bias.segments))
      ? rotations.bias.segments : [];
    const legs = indicators.rotLegs ? legsRaw
      .map((l) => {
        // ts-first: leg start_idx/end_idx are now vol-candle-ROW indices (not brick indices),
        // so mapping by idx would land the leg on the wrong brick. Legs carry start_ts/end_ts in
        // both the new and old payloads, so snap purely by time (null idx -> pure ts path).
        const x0 = resolveX(null, l.start_ts);
        const x1 = resolveX(null, l.end_ts);
        if (x0 == null || x1 == null) return null;
        return { x0, y0: l.start_price, x1, y1: l.end_price, direction: l.direction, reach: l.reach };
      })
      .filter(Boolean) : [];
    // The live segment runs to the right edge; the plugin draws it there, so its x1 is unused
    // (only x0 must resolve). Segments map by time (start_ts always served;
    // the live segment's end_ts is null and rides the `live` flag).
    const biasSegments = indicators.rotBias ? segsRaw
      .map((s) => {
        const x0 = resolveX(null, s.start_ts);
        if (x0 == null) return null;
        const x1 = s.live ? null : resolveX(null, s.end_ts);
        return { x0, x1, state: s.state, score: s.score, live: !!s.live };
      })
      .filter(Boolean) : [];
    // 70/90 reach lines: per-pivot, in each rotation's own direction, spanning just
    // that rotation -- so the 70/90 reach levels are visible at EVERY historic pivot
    // (localized, not smeared full-width). The developing rotation (after the last
    // pivot) projects both directions forward to the right edge and is labelled.
    const T = rotations ? rotations.thresholds : null;
    const reachLines = [];
    if (indicators.rotReach && T) {
      for (const l of legsRaw) {
        const t = T[l.direction];
        if (!t) continue;
        const x0 = resolveX(null, l.start_ts);   // ts-first (vc-row idx, not brick idx)
        const x1 = resolveX(null, l.end_ts);
        if (x0 == null || x1 == null) continue;
        const sgn = l.direction === 'up' ? 1 : -1;
        if (t.p50 != null) reachLines.push({ x0, x1, price: l.start_price + sgn * t.p50, level: 50, forward: false });
        if (t.p70 != null) reachLines.push({ x0, x1, price: l.start_price + sgn * t.p70, level: 70, forward: false });
        if (t.p90 != null) reachLines.push({ x0, x1, price: l.start_price + sgn * t.p90, level: 90, forward: false });
      }
      // No legs yet => no forward reach lines (the guard below short-circuits), matching the main
      // chart's OrderFlowPlugin. Only project forward once a real pivot anchors the developing rotation.
      const lastLeg = legsRaw.length ? legsRaw[legsRaw.length - 1] : null;
      const aPrice = lastLeg ? lastLeg.end_price : null;
      const aTime = lastLeg ? resolveX(null, lastLeg.end_ts) : null;   // ts-first
      if (aPrice != null && aTime != null) {
        if (T.up && T.up.p50 != null) reachLines.push({ x0: aTime, x1: null, price: aPrice + T.up.p50, level: 50, forward: true, label: `+50% (${fmt(T.up.p50)})` });
        if (T.up && T.up.p70 != null) reachLines.push({ x0: aTime, x1: null, price: aPrice + T.up.p70, level: 70, forward: true, label: `+70% (${fmt(T.up.p70)})` });
        if (T.up && T.up.p90 != null) reachLines.push({ x0: aTime, x1: null, price: aPrice + T.up.p90, level: 90, forward: true, label: `+90% (${fmt(T.up.p90)})` });
        if (T.down && T.down.p50 != null) reachLines.push({ x0: aTime, x1: null, price: aPrice - T.down.p50, level: 50, forward: true, label: `-50% (${fmt(T.down.p50)})` });
        if (T.down && T.down.p70 != null) reachLines.push({ x0: aTime, x1: null, price: aPrice - T.down.p70, level: 70, forward: true, label: `-70% (${fmt(T.down.p70)})` });
        if (T.down && T.down.p90 != null) reachLines.push({ x0: aTime, x1: null, price: aPrice - T.down.p90, level: 90, forward: true, label: `-90% (${fmt(T.down.p90)})` });
      }
    }
    // Gauge render model: the forming (unconfirmed) leg + retrace tail. Times are mapped
    // to display seconds here (vc extreme_ts is off the brick grid, so resolveX snaps it);
    // prices stay raw for the plugin's priceToCoordinate.
    const rawGauge = (rotations && rotations.gauge) ? rotations.gauge : null;
    let gauge = null;
    if (rawGauge && rawGauge.reversal_level != null) {
      const exTsRaw = rawGauge.extreme_ts;
      const exTs = exTsRaw != null ? Math.floor(exTsRaw > 1e12 ? exTsRaw / 1e9 : exTsRaw) : null;
      const lastLeg = legs.length ? legs[legs.length - 1] : null;
      gauge = {
        xPivot: lastLeg ? lastLeg.x1 : null,
        yPivot: lastLeg ? lastLeg.y1 : null,
        xExtreme: exTs != null ? resolveX(null, exTs) : null,
        yExtreme: rawGauge.extreme_price != null ? rawGauge.extreme_price : null,
        yCurrent: rawGauge.current_price != null ? rawGauge.current_price : null,
        reversalLevel: rawGauge.reversal_level,
        pReversal: rawGauge.p_reversal || 0,
        direction: rawGauge.direction,
      };
    }
    return {
      legs, biasSegments, reachLines,
      showBias: indicators.rotBias, showReach: indicators.rotReach,
      gauge, showGauge: !!(indicators.rotGauge && gauge),
    };
  }, [rotations, indicators.rotLegs, indicators.rotReach, indicators.rotBias, indicators.rotGauge, bricks, displayTimes]);

  const th = rotations && rotations.thresholds;
  const currentBias = (rotations && rotations.bias) ? rotations.bias.current : null;
  const gauge = (rotations && rotations.gauge) ? rotations.gauge : null;   // r/theta retracement gauge (descriptive)
  const showGauge = !!(indicators.rotGauge && gauge);
  // Staleness hint: the gauge is a snapshot as of its as_of_ts; flag it when older than ~2 min
  // (a quiet tape or a stalled live push) so the readout does not imply a fresh reading.
  const gaugeAgeSec = showGauge && gauge.as_of_ts != null
    ? (Date.now() / 1000 - (gauge.as_of_ts > 1e12 ? gauge.as_of_ts / 1e9 : gauge.as_of_ts))
    : 0;
  const gaugeStale = showGauge && gauge.as_of_ts != null && gaugeAgeSec > 120;
  const range = rotations ? `${rotations.start_date}→${rotations.end_date}` : '';
  // The leg-size thresholds are measured over the trailing 30d window (which may
  // differ from the rendered day), so label that panel with the threshold window.
  const thRange = rotations
    ? `${rotations.threshold_start_date || rotations.start_date}→${rotations.threshold_end_date || rotations.end_date}`
    : '';

  // Date context for the planning indicators: the actual rendered renko window
  // (its end is the session the per-session VP/TPO/OI indicators key on; levels
  // span start->end). Falls back to the canonical window before rotations load.
  const winEnd = rotations?.end_date || sessionDate;
  const winStart = rotations?.start_date || effectiveStart;
  const anySessionLevel = indicators.priorLevels ||
    (indicators.overnightLevel && (indicators.asiaLevels || indicators.londonLevels)) ||
    indicators.openingRange || indicators.initialBalance || indicators.openingPrints ||
    indicators.pivotPoints || indicators.sessionMarkers;
  const anyZeroLevel = indicators.zeroLevels;
  const emEnabled = indicators.expectedMove;
  const anyGammaLevel = indicators.gammaLevels;
  // Price-based session/OI levels (timeframe-independent, so they render on renko).
  const levelIndicators = useLevelIndicators(winStart, {
    endDate: winEnd,
    expiryMode: indicators.expiryMode,
    underlying: indicators.underlying,
    enabled: anySessionLevel || anyZeroLevel || emEnabled || anyGammaLevel,
    fetchSession: anySessionLevel,
    fetchZero: anyZeroLevel,
    fetchEm: emEnabled,
    fetchGamma: anyGammaLevel,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#0f1420', padding: 10, gap: 8 }}>
      {/* Controls */}
      <div className="chart-subbar">
        <Link to="/chart" className="nav-link">← Chart</Link>
        <span className="chart-subbar-title">Renko Rotations</span>
        <span className="chart-subbar-badge">
          {rotationsMeta?.brick_size ?? 8}pt bricks · {rotations?.theta != null
            ? `theta ${fmt(rotations.theta)}`
            : `strength ${rotations?.strength ?? 3}`}
        </span>

        {/* Brick-size selector (served sizes; 8pt default, 4pt = ~4x finer detail) */}
        <select value={brickSize}
          onChange={(e) => {
            const v = Number(e.target.value);
            setBrickSize(v);
            // Write into the shared `indicators` blob (key orderflowRotationBrickSize) so the main chart
            // picks up the same size.
            try {
              const ind = JSON.parse(localStorage.getItem('indicators') || '{}');
              ind.orderflowRotationBrickSize = v;
              localStorage.setItem('indicators', JSON.stringify(ind));
            } catch (err) { /* ignore */ }
          }}
          title="Renko brick size"
          className="nav-select">
          <option value={8}>8pt</option>
          <option value={4}>4pt</option>
        </select>

        {/* Date navigation (start date -> latest), like the main chart */}
        <div className="nav-date-group">
          <button onClick={() => setStartDate(stepWeekday(effectiveStart, -1))} title="Earlier day"
            className="nav-date-btn">‹</button>
          <input type="date" value={effectiveStart} min={minDate} max={sessionDate}
            onChange={(e) => setStartDate(e.target.value ? snapToWeekday(e.target.value) : null)}
            className="nav-date" />
          <button onClick={() => { const n = stepWeekday(effectiveStart, 1); setStartDate(n >= sessionDate ? null : n); }} title="Later day"
            className="nav-date-btn">›</button>
          <button onClick={() => setStartDate(null)} title="Reset to today (current session)"
            className="nav-date-btn nav-date-today">Today</button>
        </div>

        <TimezoneSelector timezone={timezone} onTimezoneChange={(tz) => {
          setTimezone(tz);
          try { localStorage.setItem('timezone', JSON.stringify(tz)); } catch (e) { /* ignore */ }
        }} />

        {/* Legs / 70-90 / Bias toggles now live in the side panel's Rotations group. */}
        <button onClick={() => load(startDate)} className="nav-btn">
          Refresh
        </button>
        <span className="chart-subbar-status">
          {loading ? 'loading…' : `${bricks.length} bricks · ${rotations ? rotations.n_legs : 0} legs · ${range}`}
          {error ? <span className="err"> · error: {error}</span> : ''}
        </span>
      </div>

      {/* Chart */}
      <div style={{ flex: 1, borderRadius: 10, overflow: 'hidden', border: '1px solid #2a3344', background: '#1a1f2e', position: 'relative', marginRight: panelOpen ? 300 : 0, transition: 'margin-right 0.28s cubic-bezier(0.4, 0, 0.2, 1)' }}>
        <ESChart
          candles={candles}
          date={winEnd}
          endDate={winEnd}
          timeframe="renko 8pt"
          timezone={timezone}
          expiryMode={indicators.expiryMode}
          underlying={indicators.underlying}
          showUnderlyingCompare={indicators.showUnderlyingCompare}
          underlyingCompareMode={indicators.underlyingCompareMode || 'oi'}
          underlyingCompareGreek={indicators.underlyingCompareGreek || 'gex'}
          underlyingCompareZeroLevels={indicators.underlyingCompareZeroLevels}
          underlyingCompareGammaLevels={indicators.underlyingCompareGammaLevels}
          showRotations={indicators.rotLegs || indicators.rotBias || indicators.rotReach || indicators.rotGauge}
          rotationData={rotationData}
          clearOnEmpty
          barSpacing={5}
          candleColors={{ upColor: '#22c55e', downColor: '#ef4444' }}
          hideGridLines={hideGridLines}
          onChartReady={onChartReady}
          onGreeksLoadingChange={setGreeksLoading}
          baselineLoading={baselineLoading}
          onBaselineLoadingChange={setBaselineLoading}
          // Session profiles (Volume group; TPO bars only, shared session picker)
          showVolumeProfile={indicators.volumeProfile}
          showNakedPOC={indicators.nakedPOC}
          showNakedTpoPOC={indicators.nakedTpoPOC}
          showHVNZones={indicators.hvnZones}
          vpSessionMode={indicators.vpSessionScope}
          showDeltaProfile={indicators.deltaProfile}
          showTPOProfile={indicators.tpoProfile}
          tpoRenderMode="histogram"
          tpoSessionScope={indicators.tpoSessionScope}
          tpoDisplayStyle="basic"
          showVWAP={indicators.vwap}
          showVWAPBand1={indicators.vwapBand1}
          showVWAPBand2={indicators.vwapBand2}
          vwapSessionMode={indicators.vwapSessionMode || 'full'}
          // Session levels
          showPrevDayLevels={indicators.priorLevels && indicators.prevDayLevels}
          showPriorWeekLevels={indicators.priorLevels && indicators.priorWeekLevels}
          showPriorMonthLevels={indicators.priorLevels && indicators.priorMonthLevels}
          showAsiaLevels={indicators.overnightLevel && indicators.asiaLevels}
          showLondonLevels={indicators.overnightLevel && indicators.londonLevels}
          showOpeningRange={indicators.openingRange}
          showInitialBalance={indicators.initialBalance}
          showOpeningPrints={indicators.openingPrints}
          showPivotPoints={indicators.pivotPoints}
          showSessionMarkers={indicators.sessionMarkers}
          levelData={levelIndicators}
          // Options / OI
          showZeroLevels={indicators.zeroLevels}
          showZeroLevelsLive={indicators.oiData === 'live'}
          zeroLevelGreeks={{ gex: indicators.zeroLevelsGex ?? true, dex: indicators.zeroLevelsDex ?? true, vex: indicators.zeroLevelsVex ?? true, cex: indicators.zeroLevelsCex ?? true }}
          showGammaLevels={indicators.gammaLevels}
          gammaLevelToggles={{ gvwap: indicators.gammaLevelsGvwap ?? true, band: (indicators.gammaLevelsGvwap ?? true) && (indicators.gammaLevelsBand ?? true), walls: indicators.gammaLevelsWalls ?? true }}
          showExpectedMove={indicators.expectedMove}
          showForwardOi={indicators.showForwardOi}
          showOiDistribution={indicators.showOiDistribution}
          oiDistToggles={{ callOi: indicators.oiDistCallOi ?? true, putOi: indicators.oiDistPutOi ?? true, net: indicators.oiDistNet ?? true }}
          showProjectedCurves={indicators.showProjectedCurves}
          projCurvesLive={indicators.oiData === 'live'}
          onLiveCurvesHealth={setLiveCurvesHealth}
          projectedCurveGreeks={{ gex: indicators.projGex, dex: indicators.projDex, vex: indicators.projVex, cex: indicators.projCex }}
          showExposureProfile={indicators.showExposureProfile}
          expProfileLiveSmile={indicators.oiData === 'live'}
          expProfileToggles={{ gex: indicators.expProfileGex ?? true, dex: indicators.expProfileDex ?? true, vex: indicators.expProfileVex ?? true, cex: indicators.expProfileCex ?? true }}
          exposureProfileMode={indicators.exposureProfileMode || 'dealer'}
          showExpProfileHeatmap={indicators.showExpProfileHeatmap}
          showExpProfileT1={indicators.showExpProfileT1}
          expProfileAnchorFrac={indicators.expProfileAnchorFrac ?? null}
          expProfileT1AnchorFrac={indicators.expProfileT1AnchorFrac ?? null}
          hedgeDemandAnchorFrac={indicators.hedgeDemandAnchorFrac ?? null}
          oiProfileAnchorFrac={indicators.oiProfileAnchorFrac ?? null}
          onProfileAnchorChange={handleIndicatorToggle}
          showHedgingDemand={indicators.hedgingDemand}
          showHedgeDemandHeatmap={indicators.showHedgeDemandHeatmap}
          hedgeDemandRealized={indicators.hedgeDemandRealized}
        />

        {/* Readout: directional bias + the r/theta retracement gauge + the 70/90 leg-size legend */}
        {(th || currentBias || showGauge) && (
          <div style={{
            position: 'absolute', top: 22, right: 10, background: 'rgba(15,20,32,0.85)',
            border: '1px solid #2a3344', borderRadius: 8, padding: '8px 10px', color: '#e2e8f0',
            fontSize: 12, lineHeight: 1.5, pointerEvents: 'none', minWidth: 200,
          }}>
            {currentBias && (
              <div>
                <span style={{ fontWeight: 700 }}>Bias: </span>
                <span style={{ color: biasLabel(currentBias.state)[1], fontWeight: 700 }}>
                  {biasLabel(currentBias.state)[0]}
                </span>
                <span style={{ color: '#94a3b8' }}>
                  {' '}({currentBias.score >= 0 ? '+' : ''}{fmt(currentBias.score)})
                </span>
                {currentBias.since_ts != null && (
                  <span style={{ color: '#94a3b8' }}> since {fmtSince(currentBias.since_ts)}</span>
                )}
                <div style={{ color: '#94a3b8', fontSize: 11 }}>
                  last H {fmt(currentBias.last_high)} · L {fmt(currentBias.last_low)}
                </div>
              </div>
            )}
            {showGauge && (
              <div style={{
                marginTop: currentBias ? 6 : 0, paddingTop: currentBias ? 6 : 0,
                borderTop: currentBias ? '1px solid #2a3344' : 'none',
              }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>
                  Retracement gauge{' '}
                  <span style={{ color: '#64748b', fontWeight: 400, fontSize: 11 }}>(descriptive)</span>
                </div>
                <div style={{ color: '#cbd5e1' }}>
                  pullback {fmt(gauge.r)} / {fmt(gauge.theta)} ({Math.round((gauge.p_reversal || 0) * 100)}%)
                </div>
                <div style={{ color: '#94a3b8', fontSize: 11 }}>
                  {gauge.direction === 'up' ? 'up' : 'down'} leg · reversal {fmt(gauge.reversal_level)}
                </div>
                {gaugeStale && (
                  <div style={{ color: '#f59e0b', fontSize: 11 }}>
                    as of {fmtAsOf(gauge.as_of_ts)} ET
                  </div>
                )}
              </div>
            )}
            {th && (
              <div style={{
                marginTop: (currentBias || showGauge) ? 6 : 0, paddingTop: (currentBias || showGauge) ? 6 : 0,
                borderTop: (currentBias || showGauge) ? '1px solid #2a3344' : 'none',
              }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Leg size (pts) · {thRange}</div>
                <div style={{ color: '#22c55e' }}>
                  up: n={th.up?.n ?? 0} · p50={fmt(th.up?.p50)} · p70={fmt(th.up?.p70)} · p90={fmt(th.up?.p90)}
                </div>
                <div style={{ color: '#ef4444' }}>
                  dn: n={th.down?.n ?? 0} · p50={fmt(th.down?.p50)} · p70={fmt(th.down?.p70)} · p90={fmt(th.down?.p90)}
                </div>
                <div style={{ color: '#94a3b8', marginTop: 3 }}>
                  mode up {fmt(th.up?.mode)} · dn {fmt(th.down?.mode)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Planning-style indicator side panel (same layout; per-candle profiles excluded) */}
      <IndicatorSidePanel
        groups={RENKO_GROUPS}
        storageKey="renkoIndicatorPanel"
        defaultExpandedGroups={['rotations']}
        indicators={indicators}
        onIndicatorToggle={handleIndicatorToggle}
        onApplyIndicators={handleApplyIndicators}
        greeksLoading={greeksLoading}
        onOpenChange={setPanelOpen}
        liveCurvesHealth={liveCurvesHealth}
      />
    </div>
  );
}

// Bias state -> [label, colour] for the readout badge.
const _BIAS_LABEL = {
  bullish: ['BULLISH', '#22c55e'],
  weak_bull: ['weak bull', '#4ade80'],
  bearish: ['BEARISH', '#ef4444'],
  weak_bear: ['weak bear', '#f87171'],
  neutral_broadening: ['broadening', '#94a3b8'],
  neutral_contracting: ['contracting', '#94a3b8'],
  neutral_equal: ['neutral', '#94a3b8'],
  forming: ['forming…', '#64748b'],
};

function biasLabel(state) {
  return _BIAS_LABEL[state] || ['--', '#94a3b8'];
}

// since_ts is a brick formation time (ns or sec); render HH:MM ET defensively.
function fmtSince(ts) {
  if (ts == null) return '';
  const sec = ts > 1e12 ? Math.floor(ts / 1e9) : ts;
  try {
    return new Date(sec * 1000).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York',
    });
  } catch (e) { return ''; }
}

// as_of_ts is a display second (unix sec, ns-tolerant); render HH:MM:SS ET for the gauge
// staleness hint, mirroring fmtSince's ET convention with seconds added.
function fmtAsOf(ts) {
  if (ts == null) return '';
  const sec = ts > 1e12 ? Math.floor(ts / 1e9) : ts;
  try {
    return new Date(sec * 1000).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'America/New_York',
    });
  } catch (e) { return ''; }
}

function fmt(v) {
  return (v == null) ? '-' : (Math.round(v * 100) / 100);
}
